import { prop, Vue, Options } from 'vue-class-component'
import apiService from '@/services/api-service'
import jsonPathService from '@/services/json-path-service'
import containerService from '@/services/container-service'
import { ContainerType, IPaginationResult } from '@/models/common'
import { Config } from '@/config'
import { Subscription } from 'rxjs'
import { isFilePreviewSupported, filePreviewOpenRequest, triggerDownload } from '@/services/file-preview'
import utilityService from '@/services/utility-service'
import i18n from '@/i18n'
import Pagination from '@/components/Pagination/Pagination.vue'

// Define props in a class
class Props {
  container = prop({
    type: Object,
    required: true,
    default: {}
  });
}

@Options({
  components: {
    Pagination
  }
})
export default class TableContainer extends Vue.with(Props) {
  dataToDisplay: any = [];
  canShowTable = false;
  minUrlLength = 4;
  canShowDownload: boolean = false;
  showMoreOption: boolean = true;
  maxRowCount = Config.TABLE_MAX_ROW_LENGTH;
  canShowMoreOption: boolean = false;
  dataRefreshSubscription: Subscription | null = null;
  currentPage: number = 1;
  perPage: number = Config.PAGINATION.PER_PAGE;
  totalRecords: number = 0;

  mounted () {
    this.init()
    this.clearDataRefreshSubscription()
    this.dataRefreshSubscription = apiService.detailsDataRefreshSubject.subscribe(() => {
      this.init()
      this.retryDownload()
    })
  }

  unmounted (): void {
    this.clearDataRefreshSubscription()
  }

  init () {
    if (this.container.source) {
      this.canShowTable = true
      this.getDataToDisplay(
        apiService.containerData,
        this.container.source,
        this.container.id
      )
    } else {
      this.dataToDisplay = apiService.containerData
    }

    if (!this.dataToDisplay) {
      this.dataToDisplay = []
      this.totalRecords = 0
    } else if (
      !Array.isArray(this.dataToDisplay) &&
      typeof this.dataToDisplay === 'object'
    ) {
      const result = this.dataToDisplay
      this.dataToDisplay = []
      this.dataToDisplay.push(result)
      this.totalRecords = 1
    }

    this.canShowDownload = this.hasDownloadUrlInAnyRows(this.dataToDisplay)
    this.canShowMoreOption = this.dataToDisplay.length > this.maxRowCount
  }

  get totalPages (): number {
    return this.perPage > 0
      ? Math.ceil(this.totalRecords / this.perPage)
      : 0
  }

  handlePageChange (page: number): void {
    this.currentPage = page

    const searchFormName = (this as any).$route.params.form
    const identityNumber = (this as any).$route.params.id

    if (identityNumber) {
      apiService
        .getDocument(searchFormName, identityNumber, this.currentPage, this.perPage, this.container.id)
        .then((response: any) => {
          this.getDataToDisplay(response, this.container.source, this.container.id)
        })
        .catch(() => {
          this.getDataToDisplay([], this.container.source, this.container.id)
        })
    }
  }

  getDataToDisplay (source: any, accessKey: string, containerId: number) {
    if (accessKey.includes('$.document')) {
      this.getDocumentContainerData(source, accessKey, containerId)
    } else {
      const result = jsonPathService.getValueByField(source, accessKey)
      this.dataToDisplay = result
      this.totalRecords = 1
    }
  }

  getDocumentContainerData (source: any, accessKey: string, containerId: number): void {
    if (source?.document?.[containerId]) {
      const containerData = source.document[containerId]
      if (containerData.documents && containerData.total !== undefined && containerData.total > 0) {
        this.dataToDisplay = jsonPathService.getFormattedValue(containerData.documents)
        this.totalRecords = containerData.total
      }
    } else {
      this.dataToDisplay = []
      this.totalRecords = 0
    }
  }

  getFieldByKey (source: any, accessKey: string, sanitize = true) {
    const result = jsonPathService.getValueByField(source, accessKey)
    if (sanitize) {
      return this.$sanitize(result)
    } else {
      return result
    }
  }

  getCellValue (source: any, accessKey: string) {
    const value = this.getFieldByKey(source, accessKey, false)
    if (Array.isArray(value)) {
      const result = value.toString()
      return result.length > 0 ? result : 'N/A'
    } else if (typeof value === 'number') {
      return value
    } else {
      return value || 'N/A'
    }
  }

  getColumnCount () {
    return this.container.columns.length + 1
  }

  hasDownloadUrlInAnyRows (rows: any[]) {
    if (Array.isArray(rows)) {
      const result = rows.filter(
        row => row.active_download_url?.length > this.minUrlLength
      )
      return result?.length > 0
    } else {
      return false
    }
  }

  async downloadFile (row: any) {
    row.validatingUrl = true
    const { isValid, fileType, status } = await apiService.isDownloadUrlValid(row.active_download_url)
    row.validatingUrl = false
    if (isValid) {
      this.resetRetry()
      const anchorElement = document.createElement('a')
      anchorElement.href = row.active_download_url
      anchorElement.download = row.filename
      anchorElement.click()
    } else if (status === 403) {
      apiService.handleDownloadUrlTokenExpiry()
    } else {
      this.initRetry(row)
    }
  }

  async previewFile (row: any) {
    row.validatingUrl = true
    const { isValid, fileType, status } = await apiService.isDownloadUrlValid(row.active_download_url)
    row.validatingUrl = false
    if (isValid) {
      if (isFilePreviewSupported(fileType)) { // Show file preview if supported
        filePreviewOpenRequest.next({
          fileUrl: row.active_download_url,
          name: row.filename,
          origin: 'table'
        })
      } else { // Or download
        utilityService.showToast(
          i18n.global.t('default.alert.no_preview_download'),
          'error',
          'preview-error'
        )
        this.resetRetry()
        triggerDownload(row.active_download_url, row.filename)
      }
    } else if (status === 403) {
      apiService.handleDownloadUrlTokenExpiry()
    } else {
      this.initRetry(row)
    }
  }

  resetRetry () {
    apiService.downloadRetry.retryCount = 0
    apiService.downloadRetry.retryDocumentId = null
  }

  initRetry (row: any) {
    if (apiService.downloadRetry.retryCount < apiService.MAX_DOWNLOAD_RETRY_COUNT) {
      apiService.downloadRetry.retryCount++
      apiService.downloadRetry.retryDocumentId = row.documentId
      this.$emit('onTableDataRefreshRequested')
    } else {
      this.resetRetry()
    }
  }

  retryDownload () {
    if (!apiService.downloadRetry.retryDocumentId) {
      return
    }
    const resultRow = this.dataToDisplay.find(
      (document: any) => document.documentId === apiService.downloadRetry.retryDocumentId
    )
    if (resultRow) {
      this.downloadFile(resultRow)
    }
  }

  clearDataRefreshSubscription () {
    if (this.dataRefreshSubscription && this.dataRefreshSubscription?.unsubscribe) {
      this.dataRefreshSubscription.unsubscribe()
    }
  }

  toggleShowMore () {
    this.showMoreOption = !this.showMoreOption
  }

  getDocumentId (rows: any) {
    return containerService.getDocumentId(
      ContainerType.TABLE,
      this.container.source,
      this.container.edit_url,
      rows,
      this.getCellValue.bind(this)
    )
  }
}
